import { HStack, PageContainer, Text, VStack } from '@/components/Atoms'
import { Block } from '@/components/Block'
import { DatePicker } from '@/components/DatePicker'
import { DetailsHeader } from '@/components/DetailsHeader'
import PermissionsGuard from '@/components/Guard/PermissionsGuard'
import { PendingChanges } from '@/components/PendingChanges'
import { Spinner } from '@/components/Spinner'
import usePermissions from '@/hooks/permissions/usePermissions'
import {
  PricingAssumption,
  PricingAssumptionDetail,
  pricingAssumptionByIdQueryOptions,
  usePricingAssumptionById,
  useUpdatePricingAssumption,
  useUpdatePricingAssumptionDetails,
} from '@/hooks/queries/crt/usePricingAssumptions'
import { useLastUpdatedPricingAssumptionLog } from '@/hooks/queries/useAuditTrails'
import { formatLastUpdatedAssumptionLog } from '@/lib/utils/crt'
import { queryClient } from '@/main'
import { formatDateOnly } from '@/utils/DateOnlyHelpers'
import { UpdatePricingAssumption } from '@repo/backend/crt'
import { PRODUCT_CATEGORIES } from '@repo/constants'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PinnedByVersionsNote } from '../../-components/PinnedByVersionsNote'
import { z } from 'zod'
import { MissingPriceListNote } from '../-components/MissingPriceListNote'
import { PricingAssumptionEntries } from './-components/PricingAssumptionDetails/PricingAssumptionEntries'

const pricingAssumptionDetailsSearchSchema = z.object({
  productCategory: z.array(z.enum(PRODUCT_CATEGORIES)).optional(),
  materialGroupCode: z.array(z.string()).optional(),
  brand: z.array(z.string()).optional(),
  variant: z.array(z.string()).optional(),
  productType: z.array(z.string()).optional(),
  packContentSize: z.array(z.string()).optional(),
  province: z.array(z.string()).optional(),
  unmarkedProvince: z.array(z.string()).optional(),
  channel: z.array(z.string()).optional(),
  keyword: z.string().optional(),
  sort: z
    .object({
      id: z.string(),
      desc: z.boolean(),
    })
    .optional(),
})

export const Route = createFileRoute(
  '/_auth/_modules/crt/pricing-excise/pricing-assumptions/$pricingAssumptionId',
)({
  component: PricingPricingAssumptionDetails,
  validateSearch: (search) => pricingAssumptionDetailsSearchSchema.parse(search),
  loader: async ({ params: { pricingAssumptionId } }) => {
    const pricingAssumption = await queryClient.ensureQueryData(
      pricingAssumptionByIdQueryOptions(pricingAssumptionId),
    )

    return {
      crumb: pricingAssumption.name,
    }
  },
})

function PricingPricingAssumptionDetails() {
  const { t } = useTranslation('crt')
  const { canUser } = usePermissions()
  const { pricingAssumptionId } = Route.useParams()
  const search = Route.useSearch()

  const { data, isPending, isError } = usePricingAssumptionById(pricingAssumptionId, search)
  const { data: lastUpdatedPricingAssumptionTrail } =
    useLastUpdatedPricingAssumptionLog(pricingAssumptionId)

  const [formState, setFormState] = useState({
    name: '',
    description: '',
    effectiveDate: new Date(),
  })

  const [pricingAssumption, setPricingAssumption] = useState<PricingAssumption | null>(null)

  useEffect(() => {
    if (data) {
      setFormState({
        name: data.name,
        description: data.description ?? '',
        effectiveDate: data.effectiveDate,
      })
      setPricingAssumption({
        ...data,
        effectiveDate: data.effectiveDate,
      })
    }
  }, [data])

  const hasUnsavedPriceAdjustments = pricingAssumption
    ? pricingAssumption.details.some((detail) => 'isUnsaved' in detail && detail.isUnsaved)
    : false

  const getUnsavedAssumptionChanges = (): UpdatePricingAssumption | null => {
    if (!data) return null

    const changes: UpdatePricingAssumption = {}
    let hasChanges = false

    if (formState.name !== data.name) {
      changes.name = formState.name
      hasChanges = true
    }

    if (formState.description !== (data.description ?? '')) {
      changes.description = formState.description
      hasChanges = true
    }

    if (formState.effectiveDate.getTime() !== data.effectiveDate.getTime()) {
      changes.effectiveDate = formatDateOnly(formState.effectiveDate)
      hasChanges = true
    }

    return hasChanges ? changes : null
  }

  const hasUnsavedChanges = hasUnsavedPriceAdjustments || getUnsavedAssumptionChanges() !== null

  const lastUpdatedLog = formatLastUpdatedAssumptionLog(lastUpdatedPricingAssumptionTrail)

  const { mutate: updateAssumption, isPending: isUpdateAssumptionPending } =
    useUpdatePricingAssumption({
      pricingAssumptionId: Number(pricingAssumptionId),
    })

  const { mutate: updateDetails, isPending: isUpdateDetailsPending } =
    useUpdatePricingAssumptionDetails(pricingAssumptionId)

  const handleUpdateName = (name: string) => {
    setFormState((prev) => ({ ...prev, name }))
  }

  const handleUpdateDescription = (description: string) => {
    setFormState((prev) => ({ ...prev, description }))
  }

  const handleUpdateEffectiveDate = (date: Date | undefined) => {
    if (!date) return
    setFormState((prev) => ({ ...prev, effectiveDate: date }))
  }

  const handleBulkEditSave = (
    selectedDetails: PricingAssumptionDetail[],
    updates: Partial<PricingAssumptionDetail>,
  ) => {
    if (updates.priceAdjustment === undefined || !Number.isFinite(updates.priceAdjustment)) {
      return
    }

    const priceAdjustment = updates.priceAdjustment

    const apiUpdates = selectedDetails.map((detail) => ({
      id: detail.id,
      data: {
        priceAdjustment,
      },
    }))

    updateDetails(apiUpdates)
  }

  const handleSave = () => {
    if (!pricingAssumption) return

    const unsavedAssumptionChanges = getUnsavedAssumptionChanges()

    const unsavedDetails = pricingAssumption.details.filter(
      (detail) => 'isUnsaved' in detail && detail.isUnsaved,
    )

    if (!unsavedAssumptionChanges && unsavedDetails.length === 0) return

    if (unsavedAssumptionChanges) {
      updateAssumption(unsavedAssumptionChanges)
    }

    if (unsavedDetails.length > 0) {
      const apiUpdates = unsavedDetails.map((detail) => ({
        id: detail.id,
        data: {
          priceAdjustment: detail.priceAdjustment,
        },
      }))

      updateDetails(apiUpdates)
    }
  }

  if (isPending) {
    return (
      <PageContainer className="flex items-center justify-center">
        <Spinner size="lg" />
      </PageContainer>
    )
  }

  if (isError || !pricingAssumption) {
    return (
      <PageContainer>
        <Text>{t('errors.pricingAssumptions.fetchFailed')}</Text>
      </PageContainer>
    )
  }

  return (
    <PageContainer className="pb-28">
      <Block shouldBlock={hasUnsavedChanges} />

      <MissingPriceListNote />

      <PinnedByVersionsNote driver="pricingAssumptions" entityId={pricingAssumptionId} />

      <HStack gap="sm" className="justify-between">
        <DetailsHeader
          name={formState.name}
          description={formState.description}
          onUpdateName={handleUpdateName}
          onUpdateDescription={handleUpdateDescription}
          permission="crt_pe_pricing_assumptions_edit"
        />

        <VStack gap="md" className="items-end">
          <VStack gap="xs" className="items-end">
            <Text tone="muted" size="xs">
              {t('pricingAssumptionDetails.effectiveDate')}
            </Text>
            <DatePicker
              defaultDate={formState.effectiveDate}
              onSelect={handleUpdateEffectiveDate}
              shouldWarnOnPastDate
              disabled={!canUser('crt_pe_pricing_assumptions_edit')}
            />
          </VStack>
        </VStack>
      </HStack>

      <PricingAssumptionEntries
        pricingAssumptionId={pricingAssumptionId}
        pricingAssumption={pricingAssumption}
        setPricingAssumption={setPricingAssumption}
        onBulkEditSave={handleBulkEditSave}
      />

      <PermissionsGuard required={['crt_pe_pricing_assumptions_edit']}>
        <PendingChanges
          areChangesPending={hasUnsavedChanges}
          isSavePending={isUpdateDetailsPending || isUpdateAssumptionPending}
          onSaveChanges={handleSave}
          lastUpdatedLog={lastUpdatedLog}
        />
      </PermissionsGuard>
    </PageContainer>
  )
}
